import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { ethers } from 'ethers';
import { Card, Alert, Spinner, ListGroup, Badge, Button } from 'react-bootstrap';
import type { NFT, PrivateNFTData } from '../../types';
import { CONTRACT_ADDRESS, ERC721_ABI } from '../../constants/contract';
import privateDataJson from '../../data/nftPrivateData.json';

// Convert JSON data to proper PrivateNFTData format
const nftPrivateData: PrivateNFTData = privateDataJson as PrivateNFTData;

// Type for NFT attributes
interface NFTAttribute {
  trait_type: string;
  value: string;
}

const NFTDetail: React.FC = () => {
  const { tokenId } = useParams<{ tokenId: string }>();
  const navigate = useNavigate();
  const { address, isConnected, connector } = useAccount();

  // State management
  const [nft, setNft] = React.useState<NFT | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [showCode, setShowCode] = React.useState(false);
  const [privateData, setPrivateData] = React.useState<{
    code: string;
    benefits: string[];
    accessLevel: string;
    expiryDate: string;
    qrCodeData: string;
  } | null>(null);
  const [imageError, setImageError] = React.useState(false);
  const [imageLoading, setImageLoading] = React.useState(true);

   const defaultimage = 'https://tomatco-accurate-rabbit-784.mypinata.cloud/ipfs/bafkreibr3w2arzbptaqp3wxulh5gorulri5dyiwfpc4scwyuria36rtlb4';
  // Local image imports
  const localImages: { [key: string]: string } = {
    '1': defaultimage,
    '2': defaultimage,
    '3': defaultimage
  };

  // Get provider and signer
  const getProvider = async (): Promise<ethers.providers.JsonRpcProvider | null> => {
    if (!connector) return null;
    try {
      const wagmiProvider = await connector.getProvider();
      // Cast wagmi provider to ethers-compatible type
      const provider = new ethers.providers.Web3Provider(
        wagmiProvider as unknown as ethers.providers.ExternalProvider
      );
      // Get the signer from the provider
      const signer = provider.getSigner();
      return signer.provider as ethers.providers.JsonRpcProvider;
    } catch (err) {
      console.error('Error getting provider:', err);
      return null;
    }
  };

  // Validate token ID format
  const isValidTokenId = (id: string): boolean => {
    if (!id) return false;

    // Try to parse as decimal number
    const decimalNum = Number(id);
    if (!isNaN(decimalNum) && decimalNum > 0) {
      return true;
    }

    // Try to parse as hex number (with or without 0x prefix)
    const hexId = id.toLowerCase();
    if (hexId.startsWith('0x')) {
      try {
        const hexNum = parseInt(hexId.substring(2), 16);
        return !isNaN(hexNum) && hexNum > 0;
      } catch (e) {
        return false;
      }
    }

    // Try to parse as pure hex number
    const hexRegex = /^[0-9a-f]+$/;
    if (hexRegex.test(hexId)) {
      try {
        const hexNum = parseInt(hexId, 16);
        return !isNaN(hexNum) && hexNum > 0;
      } catch (e) {
        return false;
      }
    }

    // Check if the token ID exists in our private data
    return nftPrivateData[id] !== undefined;
  };

  // Resolve IPFS URLs with better error handling
  const resolveIPFS = (url: string): { url: string; gateways: string[] } => {
    const ipfsHash = url.replace('ipfs://', '');
    const gateways = [
      `https://tomato-accurate-rabbit-784.mypinata.cloud/ipfs/${ipfsHash}`,
      `https://ipfs.io/ipfs/${ipfsHash}`,
      `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`
    ];
    return { url, gateways };
  };

  // Fetch NFT details
  const fetchNFTDetails = async () => {
    if (!tokenId || !isValidTokenId(tokenId)) {
      setError('Invalid token ID. Please provide a valid number.');
      console.log('token ID:', tokenId);
      setLoading(false);
      return;
    }

    if (!isConnected) {
      setError('Please connect your wallet to view this NFT');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const provider = await getProvider();
      if (!provider) {
        throw new Error('No wallet provider available');
      }

      // Get signer from provider
      const signer = provider.getSigner();
      
      // Initialize contract with signer
      const contract = new ethers.Contract(CONTRACT_ADDRESS, ERC721_ABI, signer);
      let tokenIdNum = tokenId;
      if (tokenId.startsWith('0x')) {
        tokenIdNum = tokenId.substring(2);
      }
      
      const privateDataForToken = (nftPrivateData as PrivateNFTData)[tokenIdNum];
      const isPrivate = privateDataForToken?.isPrivate || false;
      
      setIsPrivate(isPrivate);
      
      if (isPrivate && privateDataForToken) {
        setPrivateData({
          code: privateDataForToken.code,
          benefits: privateDataForToken.benefits,
          accessLevel: privateDataForToken.accessLevel,
          expiryDate: privateDataForToken.expiryDate,
          qrCodeData: privateDataForToken.qrCodeData
        });
      }

      try {
        let tokenURI;
        try {
          tokenURI = await contract.tokenURI(tokenIdNum);
        } catch (err) {
          throw new Error('Failed to fetch token URI');
        }
        
        const gateways = [
          tokenURI.startsWith('http') ? tokenURI : `https://tomato-accurate-rabbit-784.mypinata.cloud/ipfs/${tokenIdNum}`,
          `https://ipfs.io/ipfs/${tokenIdNum}`,
          `https://cloudflare-ipfs.com/ipfs/${tokenIdNum}`,
          `https://ipfs.io/ipfs/${tokenURI.replace('ipfs://', '')}`
        ];

        let metadata = null;
        for (const gateway of gateways) {
          try {
            const response = await fetch(gateway, { 
              headers: { 'Accept': 'application/json' },
              mode: 'cors'
            });
            if (response.ok) {
              metadata = await response.json();
              break;
            }
          } catch (err) {
            console.warn(`Failed to fetch from ${gateway}:`, err);
          }
        }

        if (metadata) {
          setNft({
            id: tokenId,
            name: metadata.name,
            description: metadata.description,
            image: metadata.image,
            attributes: metadata.attributes || [],
            ipfsGateways: gateways
          });
        } else {
          const localImage = localImages[tokenId];
          if (localImage) {
            setNft({
              id: tokenId,
              name: `NFT #${tokenId}`,
              description: 'No metadata available. Using fallback image.',
              image: localImage,
              attributes: [],
              ipfsGateways: gateways
            });
          }
        }
      } catch (err) {
        console.error('Error fetching metadata:', err);
        throw err;
      }
    } catch (err) {
      setError('Failed to load NFT details. Please try again later.');
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchNFTDetails();
  }, [tokenId, isConnected]);

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  const generateCode = () => {
    if (!isPrivate) return;
    setShowCode(true);
  };

  const copyCode = async () => {
    if (!privateData?.code) return;
    try {
      await navigator.clipboard.writeText(privateData.code);
      alert('Code copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  if (!isConnected) {
    return (
      <div className="container mt-5">
        <Alert variant="info">Please connect your wallet to view this NFT</Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  if (!nft) {
    return (
      <div className="container mt-5">
        <Alert variant="warning">NFT not found</Alert>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <Card>
        <Card.Header>
          <h2>{nft.name}</h2>
        </Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-6">
              <div className="text-center mb-4">
                {imageLoading ? (
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading image...</span>
                  </Spinner>
                ) : imageError ? (
                  <Alert variant="warning">Failed to load image</Alert>
                ) : (
                  <img
                    src={nft.image}
                    alt={nft.name}
                    className="img-fluid rounded"
                    onError={handleImageError}
                    onLoad={() => setImageLoading(false)}
                  />
                )}
              </div>
            </div>
            <div className="col-md-6">
              <p className="lead">{nft.description}</p>
              <ListGroup>
                {nft.attributes.map((attr, index) => (
                  <ListGroup.Item key={index}>
                    <strong>{attr.trait_type}:</strong> {attr.value}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {isPrivate && (
                <div>
                  <Button variant="primary" onClick={generateCode}>
                    {showCode ? 'Hide Code' : 'Show Code'}
                  </Button>
                  {showCode && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5>Access Code</h5>
                        <Button variant="secondary" size="sm" onClick={copyCode}>
                          Copy
                        </Button>
                      </div>
                      <div className="p-3 bg-light rounded">
                        {privateData?.code}
                      </div>
                    </div>
                  )}
                  {privateData && (
                    <div className="mt-3">
                      <h5>Benefits</h5>
                      <ListGroup>
                        {privateData.benefits.map((benefit, index) => (
                          <ListGroup.Item key={index}>
                            <Badge bg="success">{benefit}</Badge>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                      <p className="mt-3">
                        <strong>Access Level:</strong> {privateData.accessLevel}
                      </p>
                      <p>
                        <strong>Expiry Date:</strong> {privateData.expiryDate}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default NFTDetail;
